'use client'
import { EditorContent } from '@tiptap/react'
import { useEffect, useMemo, useRef } from 'react'
import i18n from 'i18next'

import { EditorContext } from '../../context/EditorContext.js'
import { IframeMenu } from '../../extensions/Iframe/menus/index.js'
import ImageBlockMenu from '../../extensions/ImageBlock/components/ImageBlockMenu.js'
import { InsideLinksMenu } from '../../extensions/InsideLinks/menus/index.js'
import { ColumnsMenu } from '../../extensions/MultiColumn/menus/ColumnsMenu.js'
import { SocialMediaMenu } from '../../extensions/SocialMedia/menus/index.js'
import { TableColumnMenu, TableRowMenu } from '../../extensions/Table/menus/index.js'

import VideoBlockMenu from '../../extensions/VideoBlock/components/VideoBlockMenu.js'
import { useBlockEditor } from '../../hooks/useBlockEditor.js'
import { openAssetHQType } from '../../types.js'
import { ContentItemMenu } from '../menus/ContentItemMenu/ContentItemMenu.js'
import { TextMenu } from '../menus/TextMenu/TextMenu.js'
import { LinkMenu } from '../menus/index.js'
import { AdditionalContextType, FetchSiteMetadataType, TiptapProps } from './types.js'
import i18next from 'i18next'
import ILinkPreviewMenu from '../../extensions/LinkPreview/menus/linkPreviewMenu.js'

const DEFAULT_LANG = 'en'

export const BlockEditor = ({
  handleChange,
  content,
  openAssetHQHandler,
  additionalContext,
  isEditable = true,
  autoFocus = true,
  dir,
  fetchSiteMetadata,
}: TiptapProps & {
  openAssetHQHandler?: openAssetHQType
  additionalContext?: AdditionalContextType
  fetchSiteMetadata?: FetchSiteMetadataType
  autoFocus?: boolean
  isEditable?: boolean
  dir?: 'ltr' | 'rtl'
}) => {
  const menuContainerRef = useRef(null)
  const editorRef = useRef<HTMLDivElement>(null)

  const { editor, users, characterCount, leftSidebar } = useBlockEditor({
    content: content,
    handleChange: handleChange,
    openAssetHQHandler: openAssetHQHandler,
    additionalContext: additionalContext,
    autoFocus,
    isEditable,
    dir,
  })

  const displayedUsers = users.slice(0, 3)
  const providerValue = useMemo(() => {
    return {}
  }, [])
  useEffect(() => {
    if (!additionalContext) {
      i18n.init({
        fallbackLng: DEFAULT_LANG,
        lng: DEFAULT_LANG,
        returnEmptyString: true,
        resources: {},
      })
      return
    }

    if (additionalContext.language && additionalContext.translations) {
      i18n.init({
        fallbackLng: additionalContext.language,
        lng: additionalContext.language,
        returnEmptyString: true,
        resources: {
          [additionalContext.language]: {
            translation: additionalContext.translations,
          },
        },
      })
      return
    }
    if (additionalContext.language) {
      i18n.init({
        fallbackLng: additionalContext?.language,
        lng: additionalContext?.language,
        returnEmptyString: true,
        resources: {},
      })
    }
  }, [additionalContext])
  const sectionDir = dir || i18next.dir()
  return (
    editor && (
      <EditorContext.Provider value={providerValue}>
        <div className="flex h-full overflow-visible" ref={menuContainerRef} dir={sectionDir}>
          <div className="relative flex flex-col flex-1 h-full  justify-center items-center ">
            <EditorContent
              dir={sectionDir}
              editor={editor}
              ref={editorRef}
              className="flex-1 overflow-y-visible w-full h-full outline-none outline-offset-0"
              style={{
                outline: 'none',
              }}
            />
            {isEditable && (
              <>
                <ContentItemMenu editor={editor} />
                <LinkMenu editor={editor} appendTo={menuContainerRef} />
                <TextMenu editor={editor} dir={i18next.dir()} />
                <ILinkPreviewMenu
                  editor={editor}
                  appendTo={menuContainerRef}
                  fetchSiteMetadata={fetchSiteMetadata}
                />
                <IframeMenu editor={editor} appendTo={menuContainerRef} />
                <SocialMediaMenu editor={editor} appendTo={menuContainerRef} />
                <InsideLinksMenu editor={editor} appendTo={menuContainerRef} />
                <ColumnsMenu editor={editor} appendTo={menuContainerRef} />
                <ImageBlockMenu editor={editor} appendTo={menuContainerRef} />
                <VideoBlockMenu editor={editor} appendTo={menuContainerRef} />
                <TableColumnMenu editor={editor} appendTo={menuContainerRef} />
                <TableRowMenu editor={editor} appendTo={menuContainerRef} />
              </>
            )}
          </div>
        </div>
      </EditorContext.Provider>
    )
  )
}

export default BlockEditor
